import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormControl } from 'react-bootstrap';
import { countries, citiesByCountries } from 'constants/dropdownLocations';
import styles from './SingleLocationDropdown.module.scss';

const SingleLocationDropdown = ({
  defaultLocation = '',
  onLocationChange,
  onLocationReset,
  placeholder = 'Enter the nearest city',
  showResetIcon = false,
  shouldClearOnBlur = false,
  shouldResetOnBlur = false,
  className = '',
  inputClassName = '',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const inputRef = useRef(null);

  // Initialize with default value
  useEffect(() => {
    if (defaultLocation) {
      setSearchTerm(defaultLocation);
      // Try to parse the default location to set selected location
      parseLocationString(defaultLocation);
    }
  }, [defaultLocation]);

  // Parse location string to determine if it's a city or country
  const parseLocationString = (locationStr) => {
    if (!locationStr || !countries || !citiesByCountries) return null;

    // Check if it's in "City, Country" format
    const parts = locationStr.split(', ');
    if (parts.length === 2) {
      const [cityName, countryName] = parts;

      // Find country code
      const countryCode = Object.keys(countries).find(
        (code) =>
          countries[code] &&
          typeof countries[code] === 'string' &&
          countries[code].toLowerCase() === countryName.toLowerCase()
      );

      if (
        countryCode &&
        citiesByCountries[countryCode] &&
        Array.isArray(citiesByCountries[countryCode]) &&
        citiesByCountries[countryCode].includes(cityName)
      ) {
        const location = {
          type: 'city',
          city: cityName,
          country: countryName,
          countryCode: countryCode,
          text: locationStr,
        };
        setSelectedLocation(location);
        return location;
      }
    }

    // Check if it's just a country
    const countryCode = Object.keys(countries).find(
      (code) =>
        countries[code] &&
        typeof countries[code] === 'string' &&
        countries[code].toLowerCase() === locationStr.toLowerCase()
    );

    if (countryCode && countries[countryCode]) {
      const location = {
        type: 'country',
        country: countries[countryCode],
        countryCode: countryCode,
        text: countries[countryCode],
      };
      setSelectedLocation(location);
      return location;
    }

    return null;
  };

  // Get all available locations (countries and cities)
  const allLocations = useMemo(() => {
    const locations = [];

    // Add all countries
    if (countries && typeof countries === 'object') {
      Object.entries(countries).forEach(([code, name]) => {
        if (code && name && typeof name === 'string') {
          locations.push({
            type: 'country',
            country: name,
            countryCode: code,
            text: name,
            searchText: name.toLowerCase(),
          });
        }
      });
    }

    // Add all cities with their countries
    if (
      citiesByCountries &&
      typeof citiesByCountries === 'object' &&
      countries
    ) {
      Object.entries(citiesByCountries).forEach(([countryCode, cities]) => {
        const countryName = countries[countryCode];
        if (countryName && Array.isArray(cities)) {
          cities.forEach((city) => {
            if (
              city &&
              typeof city === 'string' &&
              typeof countryName === 'string'
            ) {
              locations.push({
                type: 'city',
                city: city,
                country: countryName,
                countryCode: countryCode,
                text: `${city}, ${countryName}`,
                searchText: `${city.toLowerCase()} ${countryName.toLowerCase()}`,
              });
            }
          });
        }
      });
    }

    return locations;
  }, [countries, citiesByCountries]);

  // Filter locations based on search term
  const filteredLocations = useMemo(() => {
    if (!searchTerm) return [];

    const searchLower = searchTerm.toLowerCase();
    return allLocations
      .filter(
        (location) =>
          location.searchText && location.searchText.includes(searchLower)
      )
      .slice(0, 10); // Limit to 10 results for performance
  }, [searchTerm, allLocations]);

  // Handle location selection
  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setSearchTerm(location.text);
    setShowDropdown(false);

    // Create location data in the format expected by the browse filter
    const locationData = {
      city: location.city || '',
      country: location.country,
      country_code: location.countryCode,
      text: location.text,
      method: 'html5',
      countryOnly:
        location.type === 'country' ? 'countryOnly' : 'specificAddress',
    };

    if (onLocationChange) {
      onLocationChange(locationData);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(true);

    // If input is cleared, clear selection and notify parent
    if (!value) {
      setSelectedLocation(null);
      if (onLocationReset && shouldResetOnBlur) {
        onLocationReset();
      }
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  // Handle input blur
  const handleInputBlur = () => {
    setTimeout(() => {
      setShowDropdown(false);

      // Clear input if shouldClearOnBlur is true and no location is selected
      if (shouldClearOnBlur && searchTerm && !selectedLocation) {
        setSearchTerm('');
      }
    }, 200);
  };

  // Handle reset
  const handleReset = () => {
    setSearchTerm('');
    setSelectedLocation(null);
    setShowDropdown(false);

    if (onLocationReset) {
      onLocationReset();
    }
  };

  return (
    <div className={`${styles.locationContainer} ${className}`}>
      <div className={styles.locationInputContainer}>
        <div className={styles.dropdownWrapper}>
          <FormControl
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            className={`${styles.locationInput} ${inputClassName}`}
            autoComplete="off"
            data-1p-ignore="true"
            data-lpignore="true"
            data-form-type="other"
          />

          {showResetIcon && selectedLocation && (
            <button
              type="button"
              className={styles.resetButton}
              onClick={handleReset}
              aria-label="Clear location"
            >
              ×
            </button>
          )}

          {showDropdown && filteredLocations.length > 0 && (
            <div className={styles.dropdownMenu}>
              {filteredLocations.map((location, index) => (
                <div
                  key={`${location.type}-${location.countryCode}-${
                    location.city || location.country
                  }-${index}`}
                  className={`${styles.dropdownItem} ${
                    selectedLocation?.text === location.text
                      ? styles.selected
                      : ''
                  }`}
                  onMouseDown={() => handleLocationSelect(location)}
                >
                  {location.type === 'city' ? (
                    <>
                      {location.city}
                      {', '}
                      <span className={styles.description}>
                        {location.country}
                      </span>
                    </>
                  ) : (
                    location.country
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

SingleLocationDropdown.propTypes = {
  defaultLocation: PropTypes.string,
  onLocationChange: PropTypes.func,
  onLocationReset: PropTypes.func,
  placeholder: PropTypes.string,
  showResetIcon: PropTypes.bool,
  shouldClearOnBlur: PropTypes.bool,
  shouldResetOnBlur: PropTypes.bool,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
};

export default SingleLocationDropdown;
